import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { getLocaleFromPathname, getPathnameWithoutLocale, type Locale } from "@/lib/i18n";
import { getTranslation } from "@/lib/translations";

const SITE_URL = "https://althaus-ag.ch";
const BRAND = "W. Althaus AG";
const DEFAULT_DESC: Record<Locale, string> = {
  de: "Automation aus der Schweiz: Digital Twin, Engineering & Schaltschrankbau. Schnell, präzise, skalierbar.",
  en: "Industrial automation from Switzerland: digital twin, engineering and control cabinet construction.",
  fr: "Automatisation suisse : digital twin, ingénierie et construction d'armoires électriques.",
  it: "Automazione industriale dalla Svizzera: digital twin, ingegneria e costruzione di quadri elettrici.",
};

// SEO content keyed by internal route key, then by locale
const SEO: Record<string, Partial<Record<Locale, { title: string; description: string }>>> = {
  home: {
    de: { title: "W. Althaus AG – Industrielle Automation aus der Schweiz", description: "Seit über 50 Jahren Ihr Partner für Engineering, Software und Schaltschrankbau. Hauptsitz in Aarwangen." },
    en: { title: "W. Althaus AG – Industrial Automation from Switzerland", description: "Your partner for engineering, software and control cabinet construction for over 50 years. Headquartered in Aarwangen." },
    fr: { title: "W. Althaus AG – Automatisation industrielle suisse", description: "Votre partenaire en ingénierie, logiciel et armoires électriques depuis plus de 50 ans. Siège à Aarwangen." },
    it: { title: "W. Althaus AG – Automazione industriale svizzera", description: "Il vostro partner per ingegneria, software e quadri elettrici da oltre 50 anni. Sede ad Aarwangen." },
  },
  services: {
    de: { title: "Dienstleistungen – W. Althaus AG", description: "Engineering, Software-Engineering und Schaltschrankbau aus einer Hand für Industrie und Maschinenbau." },
    en: { title: "Services – W. Althaus AG", description: "Engineering, software engineering and control cabinet construction from a single source for industry and machinery." },
    fr: { title: "Services – W. Althaus AG", description: "Ingénierie, ingénierie logicielle et construction d'armoires électriques pour l'industrie et la machinerie." },
    it: { title: "Servizi – W. Althaus AG", description: "Ingegneria, software engineering e costruzione di quadri elettrici per industria e meccanica." },
  },
  "services/schaltschrankbau": {
    de: { title: "Schaltschrankbau – W. Althaus AG", description: "Vollautomatisierte Fertigung von Schaltschränken in höchster Schweizer Qualität, flexibel auf Ihre Anforderungen." },
    en: { title: "Control Cabinet Construction – W. Althaus AG", description: "Fully automated control cabinet manufacturing with Swiss precision, flexibly tailored to your requirements." },
    fr: { title: "Construction d'armoires – W. Althaus AG", description: "Fabrication entièrement automatisée d'armoires électriques de qualité suisse, adaptée à vos besoins." },
    it: { title: "Costruzione di quadri – W. Althaus AG", description: "Produzione completamente automatizzata di quadri elettrici di qualità svizzera, su misura." },
  },
  "services/engineering": {
    de: { title: "Hardware-Engineering – W. Althaus AG", description: "ECAD-Planung, Schaltschrank-Konzeption und Inbetriebnahme von Steuerungen für Industrie und Maschinenbau." },
    en: { title: "Hardware Engineering – W. Althaus AG", description: "ECAD planning, cabinet concepts and commissioning of control systems for industry and machinery." },
    fr: { title: "Ingénierie matérielle – W. Althaus AG", description: "Planification ECAD, conception d'armoires et mise en service de systèmes de commande." },
    it: { title: "Ingegneria hardware – W. Althaus AG", description: "Pianificazione ECAD, progettazione di quadri e messa in servizio di sistemi di controllo." },
  },
  "about/chameleonStory": {
    de: { title: "Chamäleon Story – W. Althaus AG", description: "Die Geschichte hinter unserem Chamäleon: Wandlungsfähigkeit als Markenzeichen der W. Althaus AG." },
    en: { title: "Chameleon Story – W. Althaus AG", description: "The story behind our chameleon: adaptability as the hallmark of W. Althaus AG." },
    fr: { title: "L'histoire du caméléon – W. Althaus AG", description: "L'histoire de notre caméléon : l'adaptabilité comme signature de W. Althaus AG." },
    it: { title: "La storia del camaleonte – W. Althaus AG", description: "La storia del nostro camaleonte: adattabilità come tratto distintivo di W. Althaus AG." },
  },
  "services/softwareengineering": {
    de: { title: "Software-Engineering – W. Althaus AG", description: "Massgeschneiderte Software, Visualisierungen und Datenbankapplikationen für die industrielle Automation." },
    en: { title: "Software Engineering – W. Althaus AG", description: "Custom software, visualisations and database applications for industrial automation." },
    fr: { title: "Ingénierie logicielle – W. Althaus AG", description: "Logiciels sur mesure, visualisations et applications de base de données pour l'automatisation industrielle." },
    it: { title: "Ingegneria software – W. Althaus AG", description: "Software personalizzati, visualizzazioni e applicazioni di database per l'automazione industriale." },
  },
  about: {
    de: { title: "Über uns – W. Althaus AG", description: "Lernen Sie W. Althaus AG kennen: Schweizer Familienunternehmen mit über 50 Jahren Automationserfahrung." },
    en: { title: "About us – W. Althaus AG", description: "Get to know W. Althaus AG: a Swiss family company with over 50 years of automation experience." },
    fr: { title: "À propos – W. Althaus AG", description: "Découvrez W. Althaus AG : entreprise familiale suisse avec plus de 50 ans d'expérience en automatisation." },
    it: { title: "Chi siamo – W. Althaus AG", description: "Scoprite W. Althaus AG: azienda familiare svizzera con oltre 50 anni di esperienza nell'automazione." },
  },
  "about/leitbild": {
    de: { title: "Leitbild – W. Althaus AG", description: "Werte, Mission und Vision der W. Althaus AG: Wie wir Engineering, Software und Schaltschrankbau in der Schweiz leben." },
    en: { title: "Mission Statement – W. Althaus AG", description: "Values, mission and vision of W. Althaus AG: how we approach engineering, software and control cabinet construction in Switzerland." },
    fr: { title: "Mission – W. Althaus AG", description: "Valeurs, mission et vision de W. Althaus AG : notre approche de l'ingénierie, du logiciel et des armoires électriques en Suisse." },
    it: { title: "Missione – W. Althaus AG", description: "Valori, missione e visione di W. Althaus AG: il nostro approccio a ingegneria, software e quadri elettrici in Svizzera." },
  },
  "about/team": {
    de: { title: "Management – W. Althaus AG", description: "Lernen Sie das Management der W. Althaus AG kennen: erfahrene Köpfe hinter Engineering, Software und Schaltschrankbau." },
    en: { title: "Management – W. Althaus AG", description: "Meet the management team of W. Althaus AG: the experienced people behind our engineering, software and cabinet construction." },
    fr: { title: "Management – W. Althaus AG", description: "Découvrez l'équipe de direction de W. Althaus AG : les personnes derrière l'ingénierie, le logiciel et les armoires." },
    it: { title: "Management – W. Althaus AG", description: "Conoscete il management di W. Althaus AG: i professionisti dietro ingegneria, software e quadri elettrici." },
  },
  "about/partners": {
    de: { title: "Partner-Ökosystem – W. Althaus AG", description: "Unsere Partner, Lieferanten und Technologiepartner im Überblick: starkes Ökosystem für industrielle Automation." },
    en: { title: "Partner Ecosystem – W. Althaus AG", description: "Our partners, suppliers and technology allies at a glance: a strong ecosystem for industrial automation in Switzerland." },
    fr: { title: "Écosystème de partenaires – W. Althaus AG", description: "Nos partenaires, fournisseurs et alliés technologiques : un écosystème solide pour l'automatisation industrielle." },
    it: { title: "Ecosistema di partner – W. Althaus AG", description: "I nostri partner, fornitori e alleati tecnologici: un ecosistema solido per l'automazione industriale svizzera." },
  },
  "about/anfahrt": {
    de: { title: "Anfahrt – W. Althaus AG in Aarwangen", description: "So finden Sie unseren Hauptsitz und unsere Produktion in Aarwangen: Adresse, Öffnungszeiten und Anfahrt mit Auto oder ÖV." },
    en: { title: "Directions – W. Althaus AG in Aarwangen", description: "How to find our headquarters and production site in Aarwangen: address, opening hours and directions by car or public transport." },
    fr: { title: "Accès – W. Althaus AG à Aarwangen", description: "Comment trouver notre siège et notre site de production à Aarwangen : adresse, horaires et accès en voiture ou en transports publics." },
    it: { title: "Come arrivare – W. Althaus AG ad Aarwangen", description: "Come raggiungere la nostra sede e produzione ad Aarwangen: indirizzo, orari e accesso in auto o trasporti pubblici." },
  },
  hardwareentwickler: {
    de: { title: "Hardware-Entwickler/in (m/w/d) – Stelle bei W. Althaus AG", description: "Offene Stelle als Hardware-Entwickler/in bei der W. Althaus AG in Aarwangen. ECAD, Schaltschrank-Konzeption und Inbetriebnahme." },
    en: { title: "Hardware Developer (m/f/d) – Job at W. Althaus AG", description: "Open position as Hardware Developer at W. Althaus AG in Aarwangen: ECAD, control cabinet design and commissioning." },
    fr: { title: "Développeur(se) matériel – Poste chez W. Althaus AG", description: "Poste ouvert de développeur(se) matériel chez W. Althaus AG à Aarwangen : ECAD, conception d'armoires et mise en service." },
    it: { title: "Sviluppatore hardware – Posizione W. Althaus AG", description: "Posizione aperta come sviluppatore hardware presso W. Althaus AG ad Aarwangen: ECAD, progettazione e messa in servizio." },
  },
  projektleiter: {
    de: { title: "Projektleiter/in (m/w/d) – Stelle bei W. Althaus AG", description: "Offene Stelle als Projektleiter/in bei der W. Althaus AG in Aarwangen für Automations- und Schaltschrank-Projekte." },
    en: { title: "Project Manager (m/f/d) – Job at W. Althaus AG", description: "Open position as Project Manager at W. Althaus AG in Aarwangen for automation and control cabinet projects." },
    fr: { title: "Chef(fe) de projet – Poste chez W. Althaus AG", description: "Poste ouvert de chef(fe) de projet chez W. Althaus AG à Aarwangen pour projets d'automatisation et d'armoires." },
    it: { title: "Project Manager – Posizione W. Althaus AG", description: "Posizione aperta come project manager presso W. Althaus AG ad Aarwangen per progetti di automazione e quadri." },
  },
  automatiker: {
    de: { title: "Automatiker/in (m/w/d) – Stelle bei W. Althaus AG", description: "Offene Stelle als Automatiker/in bei der W. Althaus AG in Aarwangen: Verdrahtung, Inbetriebnahme und Prüfung von Schaltschränken." },
    en: { title: "Automation Technician (m/f/d) – Job at W. Althaus AG", description: "Open position as Automation Technician at W. Althaus AG in Aarwangen: wiring, commissioning and testing of control cabinets." },
    fr: { title: "Technicien(ne) en automatisation – W. Althaus AG", description: "Poste ouvert de technicien(ne) en automatisation chez W. Althaus AG à Aarwangen : câblage, mise en service et tests d'armoires." },
    it: { title: "Tecnico/a in automazione – W. Althaus AG", description: "Posizione aperta come tecnico/a in automazione presso W. Althaus AG ad Aarwangen: cablaggio, messa in servizio e collaudo quadri." },
  },
  sustainability: {
    de: { title: "Nachhaltigkeit – W. Althaus AG", description: "Unser Engagement für nachhaltiges Wirtschaften und ressourcenschonende Produktion." },
    en: { title: "Sustainability – W. Althaus AG", description: "Our commitment to sustainable business and resource-efficient production." },
    fr: { title: "Durabilité – W. Althaus AG", description: "Notre engagement pour une activité durable et une production économe en ressources." },
    it: { title: "Sostenibilità – W. Althaus AG", description: "Il nostro impegno per un'attività sostenibile e una produzione efficiente." },
  },
  careers: {
    de: { title: "Karriere – W. Althaus AG", description: "Werden Sie Teil unseres Teams. Offene Stellen, Berufsbildung und Benefits bei der W. Althaus AG." },
    en: { title: "Careers – W. Althaus AG", description: "Join our team. Open positions, vocational training and benefits at W. Althaus AG." },
    fr: { title: "Carrières – W. Althaus AG", description: "Rejoignez notre équipe. Postes ouverts, formation et avantages chez W. Althaus AG." },
    it: { title: "Carriere – W. Althaus AG", description: "Unitevi al nostro team. Posizioni aperte, formazione e benefit presso W. Althaus AG." },
  },
  "careers/jobOpenings": {
    de: { title: "Offene Stellen – W. Althaus AG", description: "Aktuelle Stellenangebote bei der W. Althaus AG in Aarwangen." },
    en: { title: "Job Openings – W. Althaus AG", description: "Current job openings at W. Althaus AG in Aarwangen." },
    fr: { title: "Offres d'emploi – W. Althaus AG", description: "Offres d'emploi actuelles chez W. Althaus AG à Aarwangen." },
    it: { title: "Offerte di lavoro – W. Althaus AG", description: "Offerte di lavoro attuali presso W. Althaus AG ad Aarwangen." },
  },
  "careers/vocationalTraining": {
    de: { title: "Berufsbildung – W. Althaus AG", description: "Lehrstellen und Berufsbildung bei der W. Althaus AG: Automation, Elektronik und mehr." },
    en: { title: "Vocational Training – W. Althaus AG", description: "Apprenticeships and vocational training at W. Althaus AG: automation, electronics and more." },
    fr: { title: "Formation professionnelle – W. Althaus AG", description: "Apprentissages et formation chez W. Althaus AG : automatisation, électronique et plus." },
    it: { title: "Formazione professionale – W. Althaus AG", description: "Apprendistati e formazione presso W. Althaus AG: automazione, elettronica e altro." },
  },
  news: {
    de: { title: "News – W. Althaus AG", description: "Aktuelle Neuigkeiten, Projekte und Anlässe der W. Althaus AG." },
    en: { title: "News – W. Althaus AG", description: "Latest news, projects and events from W. Althaus AG." },
    fr: { title: "Actualités – W. Althaus AG", description: "Actualités, projets et événements de W. Althaus AG." },
    it: { title: "Notizie – W. Althaus AG", description: "Novità, progetti ed eventi di W. Althaus AG." },
  },
  "news/Speedpainting": {
    de: { title: "Speedpainting – Eine Momentaufnahme von Wandel und Technologie | W. Althaus AG", description: "Live-Speedpainting der Künstlerin Corinne Sutter für die W. Althaus AG: digitaler Zwilling, Chamäleon und kollaborative Robotik in einem Werk." },
    en: { title: "Speedpainting – A Snapshot of Change and Technology | W. Althaus AG", description: "Live speedpainting by Swiss artist Corinne Sutter for W. Althaus AG: digital twin, chameleon and collaborative robotics in one artwork." },
    fr: { title: "Speedpainting – Un instantané du changement et de la technologie | W. Althaus AG", description: "Speedpainting en direct de l'artiste suisse Corinne Sutter pour W. Althaus AG : jumeau numérique, caméléon et robotique collaborative." },
    it: { title: "Speedpainting – Un'istantanea di cambiamento e tecnologia | W. Althaus AG", description: "Speedpainting dal vivo dell'artista svizzera Corinne Sutter per W. Althaus AG: gemello digitale, camaleonte e robotica collaborativa." },
  },
  digitalisierungscheck: {
    de: { title: "Digitalisierungs-Check Schaltschrankbau | W. Althaus AG", description: "Wie durchgängig sind Ihre Prozesse im Schaltschrankbau? Ermitteln Sie in wenigen Minuten Ihren Digitalisierungsgrad und erhalten Sie konkrete Handlungsempfehlungen." },
    en: { title: "Digitalisation Check for Control Cabinet Building | W. Althaus AG", description: "How integrated are your control cabinet processes? Determine your digital maturity in minutes and receive concrete recommendations." },
    fr: { title: "Check de digitalisation – armoires électriques | W. Althaus AG", description: "Vos processus sont-ils continus ? Évaluez votre maturité numérique en quelques minutes et recevez des recommandations concrètes." },
    it: { title: "Check di digitalizzazione – quadri elettrici | W. Althaus AG", description: "Quanto sono integrati i vostri processi? Valutate in pochi minuti il vostro grado di digitalizzazione e ricevete raccomandazioni concrete." },
  },
  contact: {
    de: { title: "Kontakt – W. Althaus AG", description: "Kontaktieren Sie die W. Althaus AG in Aarwangen. Wir freuen uns auf Ihre Anfrage." },
    en: { title: "Contact – W. Althaus AG", description: "Get in touch with W. Althaus AG in Aarwangen. We look forward to your enquiry." },
    fr: { title: "Contact – W. Althaus AG", description: "Contactez W. Althaus AG à Aarwangen. Nous serons ravis de vous répondre." },
    it: { title: "Contatto – W. Althaus AG", description: "Contattate W. Althaus AG ad Aarwangen. Saremo lieti di rispondervi." },
  },
  documents: {
    de: { title: "Dokumente – W. Althaus AG", description: "Downloads, Datenblätter und Unterlagen der W. Althaus AG." },
    en: { title: "Documents – W. Althaus AG", description: "Downloads, datasheets and documents from W. Althaus AG." },
    fr: { title: "Documents – W. Althaus AG", description: "Téléchargements, fiches techniques et documents de W. Althaus AG." },
    it: { title: "Documenti – W. Althaus AG", description: "Download, schede tecniche e documenti di W. Althaus AG." },
  },
  imprint: {
    de: { title: "Impressum – W. Althaus AG", description: "Impressum und rechtliche Angaben der W. Althaus AG." },
    en: { title: "Imprint – W. Althaus AG", description: "Imprint and legal information of W. Althaus AG." },
    fr: { title: "Mentions légales – W. Althaus AG", description: "Mentions légales et informations sur W. Althaus AG." },
    it: { title: "Impressum – W. Althaus AG", description: "Impressum e informazioni legali di W. Althaus AG." },
  },
  privacy: {
    de: { title: "Datenschutz – W. Althaus AG", description: "Datenschutzerklärung der W. Althaus AG." },
    en: { title: "Privacy – W. Althaus AG", description: "Privacy policy of W. Althaus AG." },
    fr: { title: "Confidentialité – W. Althaus AG", description: "Politique de confidentialité de W. Althaus AG." },
    it: { title: "Privacy – W. Althaus AG", description: "Informativa sulla privacy di W. Althaus AG." },
  },
};

function deriveKey(pathname: string, locale: Locale): string {
  const stripped = getPathnameWithoutLocale(pathname).replace(/\/+$/, "") || "/";
  if (stripped === "/") return "home";
  // Static legal routes are not localized in slug
  if (stripped === "/imprint") return "imprint";
  if (stripped === "/privacy") return "privacy";

  const t = getTranslation(locale);
  const inv: Record<string, string> = {};
  Object.entries(t.routes).forEach(([k, v]) => { inv[v as string] = k; });

  const segments = stripped.slice(1).split("/").map((s) => inv[s] || s);
  return segments.join("/");
}

const localeMap: Record<Locale, string> = { de: "de_CH", en: "en_US", fr: "fr_CH", it: "it_CH" };

function buildJsonLd(key: string, title: string, description: string, canonical: string): object | null {
  // News listing page
  if (key === "news") {
    return {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: title,
      description,
      url: canonical,
      publisher: { "@type": "Organization", name: BRAND, url: SITE_URL },
    };
  }
  // News article detail (path includes /news/<slug>)
  if (/\/(news|actualites|notizie)\/[^/]+$/.test(canonical)) {
    return {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: title,
      description,
      url: canonical,
      mainEntityOfPage: canonical,
      publisher: { "@type": "Organization", name: BRAND, url: SITE_URL, logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.png` } },
    };
  }
  // Job detail pages (heuristic: under jobOpenings + specific role slugs)
  const jobMatch = /\/(hardwareentwickler|projektleiter|automatiker|hardware-developer|project-manager|automation-technician|developpeur-materiel|chef-de-projet|technicien-automatisation|sviluppatore-hardware|project-manager-it|tecnico-automazione)$/i.test(canonical);
  if (jobMatch) {
    return {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      title: title.split("–")[0].trim(),
      description,
      datePosted: new Date().toISOString().split("T")[0],
      employmentType: "FULL_TIME",
      hiringOrganization: {
        "@type": "Organization",
        name: BRAND,
        sameAs: SITE_URL,
        logo: `${SITE_URL}/logo.png`,
      },
      jobLocation: {
        "@type": "Place",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Industriestrasse 10",
          addressLocality: "Aarwangen",
          postalCode: "4912",
          addressCountry: "CH",
        },
      },
      directApply: false,
      url: canonical,
    };
  }
  // Anfahrt page: LocalBusiness for HQ + production site
  if (key === "about/anfahrt") {
    return [
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "@id": `${SITE_URL}/#hauptsitz`,
        name: `${BRAND} – Hauptsitz`,
        url: canonical,
        telephone: "+41 62 919 82 10",
        address: { "@type": "PostalAddress", streetAddress: "Jurastrasse 12", addressLocality: "Aarwangen", postalCode: "4912", addressCountry: "CH" },
        openingHoursSpecification: [
          { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday"], opens: "07:00", closes: "12:00" },
          { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday"], opens: "13:00", closes: "17:00" },
          { "@type": "OpeningHoursSpecification", dayOfWeek: "Friday", opens: "07:00", closes: "12:00" },
          { "@type": "OpeningHoursSpecification", dayOfWeek: "Friday", opens: "13:00", closes: "16:00" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "@id": `${SITE_URL}/#werk2`,
        name: `${BRAND} – Produktion`,
        url: canonical,
        telephone: "+41 62 919 82 10",
        address: { "@type": "PostalAddress", streetAddress: "Industriering 2", addressLocality: "Aarwangen", postalCode: "4912", addressCountry: "CH" },
        parentOrganization: { "@type": "Organization", name: BRAND, url: SITE_URL },
      },
    ];
  }
  return null;
}

export const RouteSEO = () => {
  const { pathname } = useLocation();
  const locale = getLocaleFromPathname(pathname);
  const key = deriveKey(pathname, locale);

  const entry = SEO[key]?.[locale] || SEO[key]?.de;
  const title = entry?.title || `${BRAND}`;
  const description = entry?.description || DEFAULT_DESC[locale];

  // Normalize canonical (no trailing slash except root)
  const cleanPath = pathname.replace(/\/+$/, "") || "/";
  const canonical = `${SITE_URL}${cleanPath === "/" ? "/" : cleanPath}`;

  // Per-route og:type: article for news detail, website otherwise
  const isNewsArticle = /\/(news|actualites|notizie)\/[^/]+$/.test(canonical);
  const ogType = isNewsArticle ? "article" : "website";

  const jsonLd = buildJsonLd(key, title, description, canonical);

  return (
    <Helmet>
      <html lang={locale} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={ogType} />
      <meta property="og:locale" content={localeMap[locale]} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

export default RouteSEO;
